import { useTranslation } from 'react-i18next'
import { AiChatHistory } from './ai-chat-history'
import { AiChatTranscript } from './ai-chat-transcript'
import { ChatComposer } from './ai-chat-composer'
import {
  AiChatAttachButton,
  AiChatFileTray,
} from './files/ai-chat-composer-files'
import { ChatMessages, PromptChips, StatusNotice } from './ai-chat-live-content'
import type { LiveAiChatScope } from './ai-chat-live-content'
import type { useAiChatLiveState } from './ai-chat-live-state'
import type { ReactNode } from 'react'

export function AiChatConversationContent({
  state,
  prompts,
  scope,
  footerAction,
}: {
  footerAction?: ReactNode
  state: ReturnType<typeof useAiChatLiveState>
  prompts: Array<string>
  scope: LiveAiChatScope
}) {
  const { t } = useTranslation('aiChat')

  if (state.historyOpen) return <AiChatHistory state={state} />

  return (
    <>
      <AiChatTranscript
        resetKey={state.activeSessionId}
        followRequest={state.followRequest}
      >
        <StatusNotice
          errorMessage={state.errorMessage}
          loading={state.loading}
          scope={scope}
        />
        <PromptChips
          prompts={prompts}
          disabled={state.blocked}
          onPickPrompt={state.submitPrompt}
        />
        {!state.messages.length && !state.loading ? (
          <p className="text-sm text-muted-foreground">{t('emptyState')}</p>
        ) : null}
        <ChatMessages
          loading={state.responding}
          messages={state.messages}
          decisionsDisabled={state.blocked}
          onAnswer={state.answerInputRequest}
          onCancel={state.cancelInputRequest}
        />
      </AiChatTranscript>
      <ChatComposer
        footerAction={footerAction}
        placeholder={
          scope === 'workspace'
            ? t('aiChat:workspaceMessagePlaceholder')
            : undefined
        }
        notice={
          scope === 'workspace' ? t('aiChat:workspaceWriteNotice') : undefined
        }
        fileTray={
          <AiChatFileTray
            files={state.files.files}
            notice={state.files.notice}
            blocked={state.files.busy || state.files.failed}
            onRemove={state.files.remove}
            onRetry={state.files.retry}
          />
        }
        attachControl={
          <AiChatAttachButton
            disabled={state.inputDisabled || state.blocked}
            onFiles={state.files.add}
          />
        }
        filesReady={state.files.readyIds.length > 0}
        filesBlocked={state.files.busy || state.files.failed}
        onFilesDropped={state.files.add}
        inputDisabled={state.inputDisabled}
        sendBlocked={state.blocked}
        draft={state.draft}
        onDraftChange={state.setDraft}
        onSubmit={state.submitDraft}
      />
    </>
  )
}
